import React, { useState, useEffect, useCallback, useRef } from 'react';
// ⬇️ [수정] useLocation 추가 (현재 경로와 다음 경로가 같은지 확인하기 위함)
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { useDirtyForm } from '../contexts/DirtyFormContext.js'; 
import './Layout.css';
// ⬇️ [추가] 아이콘 임포트
import {
  LayoutDashboard,
  UserPlus,
  Users,
  Briefcase,
  Calendar,
  Calculator,
  Receipt,
  XCircle,
  Wallet,
  Settings,
  User,
  Shield,
  ChevronDown,
  ChevronRight,
  Menu,
  LogOut,
  CreditCard,
  FileSpreadsheet,
  Send,
  FileCog,
  Mail,
  Accessibility,
  Clock,
  Bell,
  Landmark,
  UserCheck,
  Printer,
  Database,
  History,
  HardDrive,
} from 'lucide-react';
import api from '../services/api';

// --- Blocker Modal ---
// (경고 모달 스타일)
const modalStyles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  modal: {
    background: 'white', padding: '20px', borderRadius: '8px',
    width: '90%', maxWidth: '400px',
    textAlign: 'center',
    boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
  },
  buttons: { 
    display: 'flex', 
    justifyContent: 'center', // ⬅️ [수정] 중앙 정렬
    gap: '10px', 
    marginTop: '20px' 
  },
  saveButton: { // (이동하기 버튼)
    padding: '8px 12px', cursor: 'pointer', background: '#dc3545', 
    color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.9rem'
  },
  cancelButton: { // (머무르기 버튼)
    padding: '8px 12px', cursor: 'pointer', background: '#6c757d', 
    color: 'white', border: 'none', borderRadius: '4px', fontSize: '0.9rem'
  }
};

const BlockerModal = ({ onConfirm, onCancel }) => {
  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <h3 style={{ marginTop: 0 }}>페이지 이동 경고</h3>
        <p>저장되지 않은 변경 사항이 있습니다.<br />정말 이 페이지를 떠나시겠습니까?</p>
        <div style={modalStyles.buttons}>
          <button style={modalStyles.cancelButton} onClick={onCancel}>
            머무르기
          </button>
          <button style={modalStyles.saveButton} onClick={onConfirm}>
            이동하기
          </button>
        </div>
      </div>
    </div>
  );
};

const LockNoticeModal = ({ message, onClose }) => {
  return (
    <div style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <h3 style={{ marginTop: 0 }}>화면 이동 불가</h3>
        <p>{message}</p>
        <div style={modalStyles.buttons}>
          <button style={modalStyles.cancelButton} onClick={onClose}>
            확인
          </button>
        </div>
      </div>
    </div>
  );
};
// --- Blocker Modal ---


const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); 

  // ⬇️ [수정] '저장 안 됨' 상태와 '설정 함수' 모두 가져오기
  const { isDirty, setIsDirty, navigationLock, setNavigationLock } = useDirtyForm();
  
  // ⬇️ [추가] (1/3) 메뉴 이동을 가로챌 때 사용할 모달 상태
  const [navigationTarget, setNavigationTarget] = useState(null);
  const [lockNotice, setLockNotice] = useState(null);

  // ⬇️ [신규] '연차 정산' 하위 메뉴 토글 상태
  const [leaveSettleOpen, setLeaveSettleOpen] = useState(false);
  
  // ⬇️ [신규] '참여자 연차 관리' 하위 메뉴 토글 상태
  const [leaveManageOpen, setLeaveManageOpen] = useState(false);
  // ⬇️ [신규] '참여자 근태관리' 하위 메뉴 토글 상태
  const [workManageOpen, setWorkManageOpen] = useState(false);

  // ⬇️ [알림] 상태
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [toast, setToast] = useState(null); // { message, latestTitle }
  const prevUnreadRef = useRef(0);
  const hasInitialLoadRef = useRef(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      const count = res.data?.count ?? 0;
      const prev = prevUnreadRef.current;
      if (hasInitialLoadRef.current && count > prev) {
        setToast({ title: '새 알림', message: count - prev > 1 ? `알림 ${count - prev}건이 도착했습니다.` : '새 알림이 도착했습니다.' });
      }
      hasInitialLoadRef.current = true;
      prevUnreadRef.current = count;
      setUnreadCount(count);
    } catch (err) {
      // 무시 (로그인 안 됐을 수 있음)
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data || []);
    } catch (err) {
      setNotifications([]);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // 30초마다 폴링
    return () => clearInterval(interval);
  }, [user, fetchUnreadCount]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const handleNotificationClick = () => {
    setNotificationModalOpen(true);
    fetchNotifications();
  };

  const handleMarkAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      // 무시
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      const n = notifications.find((x) => x.id === id);
      setNotifications((prev) => prev.filter((x) => x.id !== id));
      if (n && !n.readAt) setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      // 무시
    }
  };

  const handleDeleteAllNotifications = async () => {
    if (!window.confirm('모든 알림을 삭제하시겠습니까?')) return;
    try {
      await api.delete('/notifications/all');
      setNotifications([]);
      setUnreadCount(0);
      prevUnreadRef.current = 0;
    } catch (err) {
      // 무시
    }
  };

  useEffect(() => {
    if (user?.role !== 'admin') return undefined;
    let stopped = false;
    const checkRestore = async () => {
      try {
        const res = await api.get('/admin/backup/status');
        if (stopped) return;
        if (res.data?.running) {
          setNavigationLock('복원이 진행 중입니다. 끝날 때까지 이 화면에 있어야 합니다.');
          if (location.pathname !== '/admin-backup') {
            navigate('/admin-backup', { replace: true });
          }
        }
      } catch {
        // 복원 중이 아니면 무시
      }
    };
    checkRestore();
    const timer = setInterval(checkRestore, 2000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [user, location.pathname, navigate, setNavigationLock]);

  const handleLogout = () => {
    if (navigationLock) {
      setLockNotice(navigationLock);
      return;
    }
    // 로그아웃 시에는 '저장 안 됨' 상태를 강제로 초기화
    setIsDirty(false, true); 
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // ⬇️ [추가] (2/3) 메뉴 클릭(이동)을 처리하는 함수 (핵심 로직)
  const handleNavigate = (e, path) => {
    e.preventDefault(); // NavLink의 기본 이동을 무조건 막음

    if (navigationLock) {
      setSidebarOpen(false);
      if (location.pathname !== path) {
        setLockNotice(navigationLock);
      }
      return;
    }
    
    // 1. 이미 같은 페이지를 눌렀거나, 저장 안 된 내용이 없으면 즉시 이동
    if (location.pathname === path || !isDirty) {
      setSidebarOpen(false); // 사이드바 닫기
      navigate(path);
      return;
    }

    // 2. 저장 안 된 내용이 있으면, 모달을 띄우기 위해 경로만 저장
    setSidebarOpen(false); // 사이드바 닫기
    setNavigationTarget(path);
  };

  // ⬇️ [추가] (3/3) 모달에서 '이동하기' / '머무르기'를 선택했을 때
  const handleModalAction = (confirmed) => {
    if (confirmed && navigationTarget) {
      // '이동하기'
      setIsDirty(false, true); // 상태 강제 초기화
      navigate(navigationTarget); // 저장했던 경로로 이동
    }
    // '머무르기' 또는 이동 완료 후 모달 닫기
    setNavigationTarget(null);
  };
  
  // ⬇️ [신규] 하위 메뉴 토글 핸들러
  const toggleSubMenu = (e) => {
    e.preventDefault();
    setLeaveSettleOpen(!leaveSettleOpen);
  };
  
  // ⬇️ [신규] '참여자 연차 관리' 하위 메뉴 토글 핸들러
  const toggleLeaveManageMenu = (e) => {
    e.preventDefault();
    setLeaveManageOpen(!leaveManageOpen);
  };
  // ⬇️ [신규] '참여자 근태관리' 하위 메뉴 토글 핸들러
  const toggleWorkManageMenu = (e) => {
    e.preventDefault();
    setWorkManageOpen(!workManageOpen);
  };
  
  // ⬇️ [수정] NavLink 리스트 수정 (아이콘 + 섹션 헤더 구조)
  const navLinks = (
    <>
      <li>
        <NavLink to="/" onClick={(e) => handleNavigate(e, '/')} className="nav-item">
          <LayoutDashboard className="nav-icon" size={20} />
          <span>대시보드</span>
        </NavLink>
      </li>

      {/* ===== 참여자 관리 섹션 ===== */}
      <li className="nav-section">
        <div className="nav-section-title">
          <Users className="nav-section-icon" size={18} />
          <span>참여자 관리</span>
        </div>
        <ul className="nav-section-list">
          <li>
            <NavLink
              to="/participant-register"
              onClick={(e) => handleNavigate(e, '/participant-register')}
              className="nav-item"
            >
              <UserPlus className="nav-icon" size={18} />
              <span>참여자 등록</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/manage"
              onClick={(e) => handleNavigate(e, '/manage')}
              className="nav-item"
            >
              <Users className="nav-icon" size={18} />
              <span>참여자 관리</span>
            </NavLink>
          </li>
        </ul>
      </li>

      {/* ===== 근무·연차 관리 섹션 ===== */}
      <li className="nav-section">
        <div className="nav-section-title">
          <Calendar className="nav-section-icon" size={18} />
          <span>근무·연차 관리</span>
        </div>
        <ul className="nav-section-list">
          {/* 참여자 근태관리 (하위: 참여자 근무관리, 스케줄표 관리) */}
          <li>
            <a
              href="#!"
              onClick={toggleWorkManageMenu}
              className={`nav-item submenu-toggle ${workManageOpen ? 'submenu-active' : ''}`}
            >
              <Briefcase className="nav-icon" size={18} />
              <span>참여자 근태 관리</span>
              {workManageOpen ? (
                <ChevronDown className="nav-icon-chevron" size={16} />
              ) : (
                <ChevronRight className="nav-icon-chevron" size={16} />
              )}
            </a>
            {workManageOpen && (
              <ul className="submenu">
                <li>
                  <NavLink
                    to="/work"
                    onClick={(e) => handleNavigate(e, '/work')}
                    className="nav-item submenu-item"
                  >
                    <Briefcase className="nav-icon" size={18} />
                    <span>참여자 근무 관리</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/schedule"
                    onClick={(e) => handleNavigate(e, '/schedule')}
                    className="nav-item submenu-item"
                  >
                    <Calendar className="nav-icon" size={18} />
                    <span>스케줄 표 관리</span>
                  </NavLink>
                </li>
              </ul>
            )}
          </li>
          <li>
            <a
              href="#!"
              onClick={toggleLeaveManageMenu}
              className={`nav-item submenu-toggle ${leaveManageOpen ? 'submenu-active' : ''}`}
            >
              <Calendar className="nav-icon" size={18} />
              <span>참여자 연차 관리</span>
              {leaveManageOpen ? (
                <ChevronDown className="nav-icon-chevron" size={16} />
              ) : (
                <ChevronRight className="nav-icon-chevron" size={16} />
              )}
            </a>
            {leaveManageOpen && (
              <ul className="submenu">
                <li>
                  <NavLink
                    to="/leave"
                    onClick={(e) => handleNavigate(e, '/leave')}
                    className="nav-item submenu-item"
                  >
                    <Calendar className="nav-icon" size={18} />
                    <span>참여자 연차 현황</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/leave-register"
                    onClick={(e) => handleNavigate(e, '/leave-register')}
                    className="nav-item submenu-item"
                  >
                    <Calendar className="nav-icon" size={18} />
                    <span>연차 사용 등록</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/leave-inquiry"
                    onClick={(e) => handleNavigate(e, '/leave-inquiry')}
                    className="nav-item submenu-item"
                  >
                    <Calendar className="nav-icon" size={18} />
                    <span>연차 신청 조회</span>
                  </NavLink>
                </li>
                {user && user.role === 'admin' && (
                  <li>
                    <NavLink
                      to="/leave-approval"
                      onClick={(e) => handleNavigate(e, '/leave-approval')}
                      className="nav-item submenu-item"
                    >
                      <Calendar className="nav-icon" size={18} />
                      <span>연차 승인 및 취소</span>
                    </NavLink>
                  </li>
                )}
              </ul>
            )}
          </li>

          {/* 연차 정산 (하위 메뉴 포함) */}
          <li>
            <a
              href="#!"
              onClick={toggleSubMenu}
              className={`nav-item submenu-toggle ${leaveSettleOpen ? 'submenu-active' : ''}`}
            >
              <Calculator className="nav-icon" size={18} />
              <span>연차 정산</span>
              {leaveSettleOpen ? (
                <ChevronDown className="nav-icon-chevron" size={16} />
              ) : (
                <ChevronRight className="nav-icon-chevron" size={16} />
              )}
            </a>
            {leaveSettleOpen && (
              <ul className="submenu">
                <li>
                  <NavLink
                    to="/leave-settle/individual"
                    onClick={(e) => handleNavigate(e, '/leave-settle/individual')}
                    className="nav-item submenu-item"
                  >
                    <Receipt className="nav-icon" size={18} />
                    <span>개별 정산</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/leave-settle/batch"
                    onClick={(e) => handleNavigate(e, '/leave-settle/batch')}
                    className="nav-item submenu-item"
                  >
                    <Receipt className="nav-icon" size={18} />
                    <span>일괄 정산</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/leave-settle/pending"
                    onClick={(e) => handleNavigate(e, '/leave-settle/pending')}
                    className="nav-item submenu-item"
                  >
                    <Clock className="nav-icon" size={18} />
                    <span>지연 정산 처리</span>
                  </NavLink>
                </li>
                {user && user.role === 'admin' && (
                  <li>
                    <NavLink
                      to="/leave-settle/cancel"
                      onClick={(e) => handleNavigate(e, '/leave-settle/cancel')}
                      className="nav-item submenu-item"
                    >
                      <XCircle className="nav-icon" size={18} />
                      <span>정산 조회 및 취소</span>
                    </NavLink>
                  </li>
                )}
              </ul>
            )}
          </li>

          <li>
            <NavLink
              to="/leave-verify"
              end
              onClick={(e) => handleNavigate(e, '/leave-verify')}
              className="nav-item"
            >
              <Calendar className="nav-icon" size={18} />
              <span>급여 검증 및 입력</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/leave-verify/history"
              onClick={(e) => handleNavigate(e, '/leave-verify/history')}
              className="nav-item"
            >
              <History className="nav-icon" size={18} />
              <span>검증 이력 조회</span>
            </NavLink>
          </li>
        </ul>
      </li>

      {/* ===== 예산 관리 섹션 ===== */}
      <li className="nav-section">
        <div className="nav-section-title">
          <Wallet className="nav-section-icon" size={18} />
          <span>예산 관리</span>
        </div>
        <ul className="nav-section-list">
          <li>
            <NavLink
              to="/budget"
              onClick={(e) => handleNavigate(e, '/budget')}
              className="nav-item"
            >
              <Wallet className="nav-icon" size={18} />
              <span>연차 예산 관리</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/labor-budget"
              onClick={(e) => handleNavigate(e, '/labor-budget')}
              className="nav-item"
            >
              <Wallet className="nav-icon" size={18} />
              <span>인건비 예산 관리</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/weekly-allowance"
              onClick={(e) => handleNavigate(e, '/weekly-allowance')}
              className="nav-item"
            >
              <Wallet className="nav-icon" size={18} />
              <span>주휴수당 예산관리</span>
            </NavLink>
          </li>
        </ul>
      </li>

      {/* ===== 급여·명세 관리 섹션 ===== */}
      <li className="nav-section">
        <div className="nav-section-title">
          <CreditCard className="nav-section-icon" size={18} />
          <span>급여·명세 관리</span>
        </div>
        <ul className="nav-section-list">
          <li>
            <NavLink
              to="/payroll/register"
              onClick={(e) => handleNavigate(e, '/payroll/register')}
              className="nav-item"
            >
              <FileSpreadsheet className="nav-icon" size={18} />
              <span>참여자 급여 관리</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/payroll/auto-transfer"
              onClick={(e) => handleNavigate(e, '/payroll/auto-transfer')}
              className="nav-item"
            >
              <Printer className="nav-icon" size={18} />
              <span>자동이체 자료 출력</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/payroll/send"
              onClick={(e) => handleNavigate(e, '/payroll/send')}
              className="nav-item"
            >
              <Send className="nav-icon" size={18} />
              <span>급여 명세표 발송</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/mailbox"
              onClick={(e) => handleNavigate(e, '/mailbox')}
              className="nav-item"
            >
              <Mail className="nav-icon" size={18} />
              <span>이메일 관리함</span>
            </NavLink>
          </li>
        </ul>
      </li>

      {/* ===== 사회보험 관리 섹션 ===== */}
      <li className="nav-section">
        <div className="nav-section-title">
          <Landmark className="nav-section-icon" size={18} />
          <span>사회보험 관리</span>
        </div>
        <ul className="nav-section-list">
          {/* 사회보험 입력: 관리자 전용 (라우트·API 동일) */}
          {user && user.role === 'admin' && (
            <li>
              <NavLink
                to="/insurance/employment"
                onClick={(e) => handleNavigate(e, '/insurance/employment')}
                className="nav-item"
              >
                <UserCheck className="nav-icon" size={18} />
                <span>사회보험 입력</span>
              </NavLink>
            </li>
          )}
          <li>
            <NavLink
              to="/insurance/export"
              onClick={(e) => handleNavigate(e, '/insurance/export')}
              className="nav-item"
            >
              <Printer className="nav-icon" size={18} />
              <span>급여자료 출력</span>
            </NavLink>
          </li>
        </ul>
      </li>

      {/* ===== 장애인 고용관리 섹션 ===== */}
      <li className="nav-section">
        <div className="nav-section-title">
          <Accessibility className="nav-section-icon" size={18} />
          <span>장애인 고용관리</span>
        </div>
        <ul className="nav-section-list">
          <li>
            <NavLink
              to="/disabled-worker-roster"
              onClick={(e) => handleNavigate(e, '/disabled-worker-roster')}
              className="nav-item"
            >
              <Users className="nav-icon" size={18} />
              <span>장애인 명단 관리</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/excluded-workers"
              onClick={(e) => handleNavigate(e, '/excluded-workers')}
              className="nav-item"
            >
              <Users className="nav-icon" size={18} />
              <span>상시적용제외근로자</span>
            </NavLink>
          </li>
        </ul>
      </li>

      {/* ===== 기본 설정 섹션 ===== */}
      <li className="nav-section">
        <div className="nav-section-title">
          <Settings className="nav-section-icon" size={18} />
          <span>기본 설정</span>
        </div>
        <ul className="nav-section-list">
          {/* 사업등록 (관리자 전용) */}
          {user && user.role === 'admin' && (
            <li>
              <NavLink
                to="/project-settings"
                onClick={(e) => handleNavigate(e, '/project-settings')}
                className="nav-item"
              >
                <FileCog className="nav-icon" size={18} />
                <span>사업 등록</span>
              </NavLink>
            </li>
          )}

          {/* 급여명세표 설정 (관리자 전용, 급여·명세 관리 → 기본 설정으로 이동) */}
          {user && user.role === 'admin' && (
            <li>
              <NavLink
                to="/payroll/settings"
                onClick={(e) => handleNavigate(e, '/payroll/settings')}
                className="nav-item"
              >
                <FileCog className="nav-icon" size={18} />
                <span>급여 명세표 설정</span>
              </NavLink>
            </li>
          )}

          {/* 이메일 설정 (관리자 전용, 급여·명세 관리 → 기본 설정으로 이동) */}
          {user && user.role === 'admin' && (
            <li>
              <NavLink
                to="/payroll/email-settings"
                onClick={(e) => handleNavigate(e, '/payroll/email-settings')}
                className="nav-item"
              >
                <Mail className="nav-icon" size={18} />
                <span>이메일 설정</span>
              </NavLink>
            </li>
          )}

          {/* 개인설정 (모든 사용자 접근 가능) */}
          <li>
            <NavLink
              to="/personal-settings"
              onClick={(e) => handleNavigate(e, '/personal-settings')}
              className="nav-item"
            >
              <User className="nav-icon" size={18} />
              <span>개인 설정</span>
            </NavLink>
          </li>

          {/* 관리자설정 (관리자 전용) */}
          {user && user.role === 'admin' && (
            <li>
              <NavLink
                to="/admin-settings"
                onClick={(e) => handleNavigate(e, '/admin-settings')}
                className="nav-item"
              >
                <Shield className="nav-icon" size={18} />
                <span>관리자 설정</span>
              </NavLink>
            </li>
          )}
          {user && user.role === 'admin' && (
            <li>
              <NavLink
                to="/admin-data-cleanup"
                onClick={(e) => handleNavigate(e, '/admin-data-cleanup')}
                className="nav-item"
              >
                <Database className="nav-icon" size={18} />
                <span>전산데이터 정리</span>
              </NavLink>
            </li>
          )}
          {user && user.role === 'admin' && (
            <li>
              <NavLink
                to="/admin-backup"
                onClick={(e) => handleNavigate(e, '/admin-backup')}
                className="nav-item"
              >
                <HardDrive className="nav-icon" size={18} />
                <span>데이터 백업 설정</span>
              </NavLink>
            </li>
          )}
        </ul>
      </li>
    </>
  );

  return (
    <div className="layout">
      {/* 1. 사이드바 */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* ⬇️ [수정] 로고 링크도 handleNavigate 적용 */}
        <NavLink to="/" className="logo-link" onClick={(e) => handleNavigate(e, '/')}>
          <div className="logo-container">
            <img src={`${process.env.PUBLIC_URL || ''}/logo192.png`} alt="로고" className="logo-icon" />
            <div className="logo-title">
              노인일자리<br />참여자 관리 시스템
            </div>
          </div>
        </NavLink>
        
        <nav>
          <ul>{navLinks}</ul>
        </nav>
      </aside>

      {/* 2. 모바일용 오버레이 (사이드바 열렸을 때) */}
      <div className="overlay" onClick={toggleSidebar}></div>

      {/* 3. 메인 콘텐츠 (헤더 + 페이지) */}
      <div className="main-content">
        <header className="header">
          <button className="menu-toggle" onClick={toggleSidebar}>
            <Menu size={24} />
          </button>
          
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
            {user && (
              <button
                type="button"
                className={`notification-toggle ${unreadCount > 0 ? 'notification-has-unread' : ''}`}
                onClick={handleNotificationClick}
                title="알림"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
              </button>
            )}
            {user && (
              <span className="user-info">
                {user.name || user.username}님
              </span>
            )}
            <button className="logout-button" onClick={handleLogout}>
              <LogOut size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              로그아웃
            </button>
          </div>
        </header>

        <main className="page-content">
          <Outlet />
        </main>
      </div>

      {/* ⬇️ [추가] navigationTarget이 설정되면 모달 렌더링 */}
      {navigationTarget && (
        <BlockerModal
          onConfirm={() => handleModalAction(true)}
          onCancel={() => handleModalAction(false)}
        />
      )}
      {lockNotice && (
        <LockNoticeModal message={lockNotice} onClose={() => setLockNotice(null)} />
      )}

      {/* ⬇️ [알림] 토스트 팝업 */}
      {toast && (
        <div
          className="notification-toast"
          onClick={() => { setToast(null); setNotificationModalOpen(true); fetchNotifications(); }}
        >
          <div className="notification-toast-inner">
            <strong>🔔 {toast.title}</strong>
            <p>{toast.message}</p>
            <span className="notification-toast-btn">확인</span>
          </div>
        </div>
      )}

      {/* ⬇️ [알림] 모달 */}
      {notificationModalOpen && (
        <div className="notification-modal-overlay" onClick={() => setNotificationModalOpen(false)}>
          <div className="notification-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notification-modal-header">
              <h3>알림</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {notifications.length > 0 && (
                  <button
                    type="button"
                    className="notification-delete-all-btn"
                    onClick={handleDeleteAllNotifications}
                  >
                    전체 삭제
                  </button>
                )}
                <button
                  type="button"
                  className="notification-settings-link"
                  onClick={() => { setNotificationModalOpen(false); navigate('/personal-settings'); }}
                >
                  설정
                </button>
                <button type="button" className="notification-modal-close" onClick={() => setNotificationModalOpen(false)}>×</button>
              </div>
            </div>
            <div className="notification-modal-body">
              {notifications.length === 0 ? (
                <p className="notification-empty">알림이 없습니다.</p>
              ) : (
                <ul className="notification-list">
                  {notifications.map((n) => (
                    <li key={n.id} className={n.readAt ? 'notification-read' : 'notification-unread'}>
                      <div className="notification-item-content">
                        <strong>{n.title}</strong>
                        <p>{n.message}</p>
                        <span className="notification-date">{n.createdAt ? new Date(n.createdAt).toLocaleString('ko-KR') : ''}</span>
                      </div>
                      <div className="notification-item-actions">
                        {!n.readAt && (
                          <button type="button" onClick={() => handleMarkAsRead(n.id)}>읽음</button>
                        )}
                        <button type="button" onClick={() => handleDeleteNotification(n.id)}>삭제</button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;